import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {
    Typography,
    TextField,
    Select,
    InputAdornment,
    MenuItem,
    Chip,
    FormControl,
    Checkbox,
    ListItemText,
    Button,
    FormControlLabel,
    Avatar,
    Skeleton,
    Modal,
    CircularProgress,
    Box,
} from '@mui/material'
import {Search, CheckCircle} from '@mui/icons-material'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter, TableSkeleton, SkeletonContainer, TextSkeleton, ChipSkeleton, ButtonSkeleton} from '../../../../components'
import {format} from 'date-fns'
import {useMobile} from '../../../../hooks'
import {ignoreTimezone, formatTime, getInitials, getColorFromName, parseError} from '../../../../utils/helpers'
import {noResultsFound} from '../../../../assets'
import {evvApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {DatePicker as DatePickerAntd} from 'antd'
import {useNavigate} from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'

const isoTime = iso => (iso ? format(new Date(iso), 'HH:mm') : null)

const SCROLL_PAGE_SIZE = 10

const EMPTY_STATES = {
    all_status: {title: 'No visits found', description: 'There are no visits matching your current filters.'},
    pending_review: {
        title: 'No visits pending review',
        description: 'All completed visits have been reviewed and are ready for the next step.',
    },
    submitted: {
        title: 'No visits in flight',
        description: 'Visits queued for HHAX, and those already submitted and awaiting a response, appear here.',
    },
    approved: {title: 'No approved visits yet', description: 'Visits accepted by HHAX will appear here.'},
    rejected: {title: 'No rejected visits', description: 'Great! There are no rejected visits or unresolved EVV exceptions.'},
    cancelled: {title: 'No cancelled visits', description: 'There are no cancelled visits in the selected period.'},
    blocked: {title: 'No blocked visits', description: 'Great! There are no visits blocked from submission.'},
}

// The submitted tab holds two different facts: a visit HHAX already has, and
// one still sitting in our own queue (waiting on a caregiver sync, or simply
// not picked up yet). An admin acts on neither, which is why they share a tab —
// but calling the second one "Submitted" tells an agency its EVV obligation is
// discharged when nothing has left the building. The backend sends `queued` for
// exactly this distinction.
const statusChip = (value, queued) => {
    if (value === 'pending_review') {
        return <Chip label="Pending for Review" variant="filled" color="warning" />
    } else if (value === 'submitted') {
        return queued ? <Chip label="Queued" variant="outlined" color="info" /> : <Chip label="Submitted" variant="filled" color="info" />
    } else if (value === 'approved') {
        return <Chip label="Approved" variant="filled" color="success" />
    } else if (value === 'rejected') {
        return <Chip label="Rejected" variant="filled" color="error" />
    } else if (value === 'cancelled') {
        return <Chip label="Cancelled" variant="filled" color="error" />
    } else if (value === 'blocked') {
        return <Chip label="Blocked" variant="filled" color="error" />
    } else if (value === 'not_required') {
        return <Chip label="Not Required" variant="outlined" sx={{color: 'text.secondary', borderColor: 'divider'}} />
    } else if (value === 'withdrawn') {
        return (
            <Chip
                label="Withdrawn"
                variant="filled"
                sx={theme => ({backgroundColor: theme.palette.grey[300], color: theme.palette.text.secondary})}
            />
        )
    }
    return null
}

const VISIT_ACTIONS = {
    review: {
        call: evvApi.bulkReviewVisits,
        title: n => `Clear ${n > 1 ? `${n} visits` : 'this visit'} for review?`,
        description: 'The visit will be queued and submitted to HHAX.',
        confirmLabel: n => (n > 1 ? `Review (${n})` : 'Review'),
        confirmColor: 'primary',
        successTitle: n => (n > 1 ? `${n} Visits Reviewed!` : 'Visit Reviewed!'),
        successDescription: 'Queued for submission to HHAX.',
    },
    resubmit: {
        call: evvApi.bulkResubmitVisits,
        title: n => `Resubmit ${n > 1 ? `${n} visits` : 'this visit'} to HHAX?`,
        description: 'This forces the visit past the unchanged-payload gate and resends it to HHAX.',
        confirmLabel: n => (n > 1 ? `Resubmit (${n})` : 'Resubmit'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Resubmitted!` : 'Visit Resubmitted!'),
        successDescription: 'Queued for resubmission to HHAX.',
    },
    escalate: {
        call: evvApi.bulkEscalateVisits,
        title: n => `Escalate ${n > 1 ? `${n} visits` : 'this visit'} to the payer?`,
        description: 'This records that a human is chasing it. It does not retry the submission or change the status.',
        confirmLabel: n => (n > 1 ? `Escalate (${n})` : 'Escalate'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Escalated!` : 'Visit Escalated!'),
        successDescription: 'Noted in the visit activity trail.',
        requiresNote: true,
    },
    cancel: {
        call: evvApi.bulkCancelVisits,
        title: n => `Cancel ${n > 1 ? `${n} visits` : 'this visit'}?`,
        description: 'If HHAX already accepted the visit this withdraws it; otherwise it simply stops here before ever reaching HHAX.',
        confirmLabel: n => (n > 1 ? `Cancel (${n})` : 'Cancel'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Cancelled!` : 'Visit Cancelled!'),
        successDescription: 'Removed from the EVV submission queue.',
    },
}

// Mirrors the mobile visit card below.
const VisitCardsSkeleton = ({checkbox}) => (
    <SkeletonContainer>
        {['48%', '40%', '56%'].map((width, index) => (
            <div className="card" key={index}>
                <div className="top-card">
                    <ChipSkeleton width={120} height={32} />
                    {checkbox && <Skeleton variant="rounded" width={18} height={18} sx={{m: '12px', borderRadius: 0.5}} />}
                </div>
                <TextSkeleton width={width} />
                <div className="form">
                    <TextSkeleton width={72} fontSize={15} />
                    <TextSkeleton width={12} />
                </div>
                <TextSkeleton width="62%" fontSize={15} />
                <TextSkeleton width="58%" fontSize={15} />
                <div className="form">
                    <TextSkeleton width={76} />
                    <TextSkeleton width={110} />
                </div>
                <div className="form">
                    <ButtonSkeleton width={72} height={30} />
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

const VisitActionDialog = ({open, setOpen, action, loadData, items = []}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const config = VISIT_ACTIONS[action]
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                note: config?.requiresNote ? yup.string().required('Please describe what you are escalating.') : yup.string(),
            }),
        ),
    })

    const submit = data => {
        setLoading(true)
        const visitIds = items.map(item => item?.visitId)
        config
            .call(config?.requiresNote ? {visitIds, note: data?.note} : {visitIds})
            .then(({data: {queued, failed}}) => {
                setLoading(false)
                setOpen(false)
                form.reset()
                if (failed > 0) {
                    setMessageBox({
                        status: 'error',
                        title: 'Some visits could not be updated',
                        description: `${queued} queued, ${failed} failed. Refresh to see which ones.`,
                    })
                } else {
                    setMessageBox({status: 'success', title: config.successTitle(items?.length), description: config.successDescription})
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                form.reset()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                loadData()
            })
    }

    if (!config) {
        return null
    }

    return (
        <Modal open={open}>
            <div className="action-container">
                <div className="action-content">
                    <Typography variant="h6" color="text.primary" style={{textAlign: 'center'}}>
                        {config.title(items?.length)}
                    </Typography>
                    <br />
                    <Typography variant="body2" color="text.secondary" style={{textAlign: 'center'}}>
                        {config.description}
                    </Typography>
                    {config.requiresNote && (
                        <>
                            <br />
                            <br />
                            <Typography variant="inputLabel" color="text.secondary">
                                Note
                            </Typography>
                            <TextField
                                placeholder="What are you escalating, and to whom?"
                                fullWidth
                                multiline
                                rows={4}
                                error={Boolean(form.formState.errors?.note)}
                                helperText={form.formState.errors?.note?.message}
                                {...form.register('note')}
                            />
                        </>
                    )}
                    <br />
                    <br />
                    <div className="action-buttons">
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                setOpen(false)
                                form.reset()
                            }}>
                            Go Back
                        </Button>
                        <Button variant="contained" color={config.confirmColor} fullWidth onClick={form.handleSubmit(submit)}>
                            {config.confirmLabel(items?.length)}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default ({status, clients = [], caregivers = [], services = [], startDate, setStartDate, endDate, setEndDate}) => {
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {RangePicker} = DatePickerAntd
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const [searchTyping, setSearchTyping] = useState('')
    const [search, setSearch] = useState('')
    const [selectedClients, setSelectedClients] = useState([])
    const [searchClientTerm, setSearchClientTerm] = useState('')
    const filteredClients = clients.filter(item => item?.name?.toLowerCase().includes(searchClientTerm?.toLowerCase()))
    const [selectedCaregivers, setSelectedCaregivers] = useState([])
    const [searchCaregiverTerm, setSearchCaregiverTerm] = useState('')
    const filteredCaregivers = caregivers.filter(item => item?.name?.toLowerCase().includes(searchCaregiverTerm?.toLowerCase()))
    const [selectedServices, setSelectedServices] = useState([])
    const [searchServiceTerm, setSearchServiceTerm] = useState('')
    const filteredServices = services.filter(item => item?.name?.toLowerCase().includes(searchServiceTerm?.toLowerCase()))
    const [rowSelectionModel, setRowSelectionModel] = useState({type: 'include', ids: new Set()})
    const [scrollStep, setScrollStep] = useState(1)
    const menuRef = useRef(null)
    const [actionOpen, setActionOpen] = useState(null)
    const [actionItems, setActionItems] = useState([])

    useEffect(() => {
        setSelectedClients(clients.map(r => r.id))
    }, [clients])

    useEffect(() => {
        setSelectedCaregivers(caregivers.map(r => r.id))
    }, [caregivers])

    useEffect(() => {
        setSelectedServices(services.map(r => r.id))
    }, [services])

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(searchTyping), 500)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    useEffect(() => {
        setPage(0)
        setScrollStep(1)
        clearAllSelections()
    }, [status, search, selectedClients, selectedCaregivers, selectedServices, startDate, endDate])

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadData()
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, page, pageSize, status, search, selectedClients, selectedCaregivers, selectedServices, startDate, endDate])

    // refresh: reload after an action on rows already on screen; keep them visible behind the spinner.
    const loadData = (refresh = false) => {
        if (!branchId) {
            return
        }
        const id = ++requestId.current
        if (refresh) {
            setLoading(true)
        } else {
            setSkeleton(true)
        }
        evvApi
            .getVisits({
                branch_id: branchId,
                status: status === 'all_status' ? null : status,
                search: search || null,
                client_id: selectedClients?.filter(Boolean)?.length === clients?.length ? null : selectedClients?.filter(Boolean)?.join(','),
                caregiver_id:
                    selectedCaregivers?.filter(Boolean)?.length === caregivers?.length ? null : selectedCaregivers?.filter(Boolean)?.join(','),
                service_id: selectedServices?.filter(Boolean)?.length === services?.length ? null : selectedServices?.filter(Boolean)?.join(','),
                from: startDate?.format('YYYY-MM-DD'),
                to: endDate?.format('YYYY-MM-DD'),
                page: page + 1,
                page_size: pageSize,
            })
            .then(({data: {visits, total}}) => {
                if (refresh) setLoading(false)
                if (id !== requestId.current) return
                setRows(visits || [])
                setRowCount(total || 0)
                setSkeleton(false)
            })
            .catch(error => {
                if (refresh) setLoading(false)
                if (id !== requestId.current) return
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                setSkeleton(false)
            })
    }

    const mobileRows = rows.slice(0, scrollStep * SCROLL_PAGE_SIZE)
    const hasMoreScroll = rows.length > mobileRows.length
    const visibleRows = mobile ? mobileRows : rows

    const selectedRows =
        rowSelectionModel?.type === 'include'
            ? visibleRows.filter(row => rowSelectionModel?.ids?.has(row?.visitId))
            : visibleRows.filter(row => !rowSelectionModel?.ids?.has(row?.visitId))

    const clientChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredClients.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedClients.includes(id))
            if (isAllSelected) {
                setSelectedClients(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedClients(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedClients(value)
        }
    }

    const renderClientLabel = (selecteds = []) => {
        if (clients.length > 0 && selecteds.length === clients.length) {
            return 'All Clients'
        }
        return clients
            .filter(r => selecteds.includes(r.id))
            .map(r => r.name)
            .join(', ')
    }

    const caregiverChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredCaregivers.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedCaregivers.includes(id))
            if (isAllSelected) {
                setSelectedCaregivers(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedCaregivers(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedCaregivers(value)
        }
    }

    const renderCaregiverLabel = (selecteds = []) => {
        if (caregivers.length > 0 && selecteds.length === caregivers.length) {
            return 'All Caregivers'
        }
        return caregivers
            .filter(r => selecteds.includes(r.id))
            .map(r => r.name)
            .join(', ')
    }

    const serviceChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredServices.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedServices.includes(id))
            if (isAllSelected) {
                setSelectedServices(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedServices(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedServices(value)
        }
    }

    const renderServiceLabel = (selecteds = []) => {
        if (services.length > 0 && selecteds.length === services.length) {
            return 'All Services'
        }
        return services
            .filter(r => selecteds.includes(r.id))
            .map(r => r.name)
            .join(', ')
    }

    const selectAll = () => {
        setRowSelectionModel({type: 'include', ids: new Set(visibleRows.map(row => row.visitId))})
    }

    const clearAllSelections = () => {
        setRowSelectionModel({type: 'include', ids: new Set()})
    }

    const openAction = (action, items) => {
        setActionItems(items)
        setActionOpen(action)
    }

    const rowAction = row => {
        if (row.status === 'pending_review') {
            return (
                <Button size="small" variant="outlined" onClick={() => openAction('review', [row])}>
                    Review
                </Button>
            )
        } else if (row.status === 'rejected') {
            return (
                <Button size="small" variant="outlined" color="error" onClick={() => openAction('resubmit', [row])}>
                    Resolve
                </Button>
            )
        } else if (row.status === 'cancelled') {
            return (
                <Button size="small" variant="outlined" color="error" onClick={() => openAction('resubmit', [row])}>
                    Resubmit
                </Button>
            )
        } else if (row.status === 'blocked') {
            return (
                <Button size="small" variant="outlined" color="error" onClick={() => openAction('escalate', [row])}>
                    Escalate
                </Button>
            )
        }
        return '-'
    }

    const columns = [
        {
            field: 'date',
            headerName: 'Date',
            flex: 0.8,
            renderCell: ({row}) => format(new Date(ignoreTimezone(row?.date)), 'MM/dd/yyyy'),
        },
        {
            field: 'client',
            headerName: 'Client',
            flex: 1,
            valueGetter: (value, row) => row?.client?.name,
        },
        {
            field: 'caregiver',
            headerName: 'Caregiver',
            flex: 1,
            valueGetter: (value, row) => row?.caregiver?.name,
        },
        {
            field: 'service',
            headerName: 'Services',
            flex: 1,
            valueGetter: (value, row) => row?.service,
        },
        {
            field: 'total_cost',
            headerName: 'Total',
            flex: 0.6,
            sortable: false,
            renderCell: () => '-',
        },
        {
            field: 'schedule_time',
            headerName: 'STime',
            flex: 1,
            sortable: false,
            renderCell: ({row}) => (
                <div className="schedule">
                    <Typography variant="body2" color="text.primary">
                        {formatTime(isoTime(row?.scheduled?.start))}-{formatTime(isoTime(row?.scheduled?.end))}
                    </Typography>
                </div>
            ),
        },
        {
            field: 'act_time',
            headerName: 'Act Time',
            flex: 1.2,
            sortable: false,
            renderCell: ({row}) =>
                row?.actual?.start ? (
                    <div className="act-time">
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2" color="text.primary">
                            {formatTime(isoTime(row?.actual?.start))}-{formatTime(isoTime(row?.actual?.end))}
                        </Typography>
                    </div>
                ) : (
                    '-'
                ),
        },
        ...(status === 'all_status'
            ? [
                  {
                      field: 'status',
                      headerName: 'EVV Status',
                      flex: 1,
                      renderCell: ({row}) => statusChip(row?.status, row?.queued),
                  },
              ]
            : []),
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.8,
            sortable: false,
            headerAlign: 'center',
            renderCell: ({row}) => (
                <div className="action-column" onClick={e => e.stopPropagation()}>
                    {rowAction(row)}
                </div>
            ),
        },
    ]

    const hasCheckboxSelection = ['pending_review', 'rejected', 'cancelled', 'blocked'].includes(status)
    const emptyState = EMPTY_STATES[status] || EMPTY_STATES.all_status

    return (
        <>
            <div className={mobile ? 'filter-mobile' : 'filter'}>
                <TextField
                    placeholder="Search client or caregiver or service name ..."
                    style={{flex: 2}}
                    fullWidth={mobile}
                    onChange={e => setSearchTyping(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <FormControl fullWidth={mobile} style={{flex: 1}}>
                    <Select
                        multiple
                        value={selectedClients}
                        onChange={clientChange}
                        renderValue={renderClientLabel}
                        MenuProps={{PaperProps: {style: {maxHeight: 300}, ref: menuRef}}}
                        onOpen={() => {
                            setTimeout(() => {
                                if (menuRef?.current) menuRef.current.scrollTop = 0
                            }, 0)
                        }}>
                        <MenuItem disableRipple disableTouchRipple>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search client name..."
                                value={searchClientTerm}
                                onChange={e => setSearchClientTerm(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                autoFocus
                            />
                        </MenuItem>
                        <MenuItem value="all">
                            <Checkbox
                                checked={filteredClients.length > 0 && filteredClients.every(c => selectedClients.includes(c.id))}
                                indeterminate={
                                    filteredClients.some(c => selectedClients.includes(c.id)) &&
                                    !filteredClients.every(c => selectedClients.includes(c.id))
                                }
                            />
                            <ListItemText primary="Select All" />
                        </MenuItem>
                        {filteredClients.map(item => (
                            <MenuItem key={item.id} value={item.id}>
                                <Checkbox checked={selectedClients.includes(item.id)} />
                                <div className="people-card">
                                    {item?.avatar_url ? (
                                        <Avatar src={item?.avatar_url} sx={{width: 24, height: 24}} />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                backgroundColor: getColorFromName(item?.name),
                                                width: 24,
                                                height: 24,
                                                fontSize: 12,
                                            }}>
                                            {getInitials(item?.name)}
                                        </Avatar>
                                    )}
                                    <Typography variant="inputText">{item?.name}</Typography>
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth={mobile} style={{flex: 1}}>
                    <Select
                        multiple
                        value={selectedCaregivers}
                        onChange={caregiverChange}
                        renderValue={renderCaregiverLabel}
                        MenuProps={{PaperProps: {style: {maxHeight: 300}, ref: menuRef}}}
                        onOpen={() => {
                            setTimeout(() => {
                                if (menuRef?.current) menuRef.current.scrollTop = 0
                            }, 0)
                        }}>
                        <MenuItem disableRipple disableTouchRipple>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search caregiver name..."
                                value={searchCaregiverTerm}
                                onChange={e => setSearchCaregiverTerm(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                autoFocus
                            />
                        </MenuItem>
                        <MenuItem value="all">
                            <Checkbox
                                checked={filteredCaregivers.length > 0 && filteredCaregivers.every(c => selectedCaregivers.includes(c.id))}
                                indeterminate={
                                    filteredCaregivers.some(c => selectedCaregivers.includes(c.id)) &&
                                    !filteredCaregivers.every(c => selectedCaregivers.includes(c.id))
                                }
                            />
                            <ListItemText primary="Select All" />
                        </MenuItem>
                        {filteredCaregivers.map(item => (
                            <MenuItem key={item.id} value={item.id}>
                                <Checkbox checked={selectedCaregivers.includes(item.id)} />
                                <div className="people-card">
                                    {item?.avatar_url ? (
                                        <Avatar src={item?.avatar_url} sx={{width: 24, height: 24}} />
                                    ) : (
                                        <Avatar
                                            sx={{
                                                backgroundColor: getColorFromName(item?.name),
                                                width: 24,
                                                height: 24,
                                                fontSize: 12,
                                            }}>
                                            {getInitials(item?.name)}
                                        </Avatar>
                                    )}
                                    <Typography variant="inputText">{item?.name}</Typography>
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth={mobile} style={{flex: 1}}>
                    <Select
                        multiple
                        value={selectedServices}
                        onChange={serviceChange}
                        renderValue={renderServiceLabel}
                        MenuProps={{PaperProps: {style: {maxHeight: 300}, ref: menuRef}}}
                        onOpen={() => {
                            setTimeout(() => {
                                if (menuRef?.current) menuRef.current.scrollTop = 0
                            }, 0)
                        }}>
                        <MenuItem disableRipple disableTouchRipple>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search service name..."
                                value={searchServiceTerm}
                                onChange={e => setSearchServiceTerm(e.target.value)}
                                onKeyDown={e => e.stopPropagation()}
                                autoFocus
                            />
                        </MenuItem>
                        <MenuItem value="all">
                            <Checkbox
                                checked={filteredServices.length > 0 && filteredServices.every(c => selectedServices.includes(c.id))}
                                indeterminate={
                                    filteredServices.some(c => selectedServices.includes(c.id)) &&
                                    !filteredServices.every(c => selectedServices.includes(c.id))
                                }
                            />
                            <ListItemText primary="Select All" />
                        </MenuItem>
                        {filteredServices.map(item => (
                            <MenuItem key={item.id} value={item.id}>
                                <Checkbox checked={selectedServices.includes(item.id)} />
                                <div className="service-display">
                                    <Chip
                                        label={item?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                                        color="action.selected"
                                        variant="filled"
                                        size="small"
                                        style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                    />
                                    <Typography variant="inputText">{item?.name}</Typography>
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth={mobile} style={{flex: 1}}>
                    <RangePicker
                        value={startDate && endDate ? [startDate, endDate] : [null, null]}
                        onChange={e => {
                            const [start_date, end_date] = e
                            setStartDate(start_date)
                            setEndDate(end_date)
                        }}
                        format="MM/DD/YYYY"
                        allowClear={false}
                        style={{
                            borderRadius: 16,
                            border: '1px solid #0000003B',
                            backgroundColor: '#fbfbfb',
                            height: 56,
                            color: '#00000099',
                        }}
                        placement="bottomRight"
                    />
                </FormControl>
            </div>
            <br />
            {selectedRows?.length > 0 && hasCheckboxSelection && (
                <div className="selection-toolbox">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rowSelectionModel?.ids?.size === visibleRows?.length && visibleRows?.length > 0}
                                onChange={e => (e.target.checked ? selectAll() : clearAllSelections())}
                            />
                        }
                        label={
                            <Typography variant="h8" color="text.primary">
                                Select All
                            </Typography>
                        }
                    />
                    {status === 'pending_review' && (
                        <div className="selection-toolbox-options">
                            <Button variant="outlined" color="error" onClick={() => openAction('cancel', selectedRows)}>
                                Move to Cancel
                            </Button>
                            <Button variant="contained" onClick={() => openAction('review', selectedRows)}>
                                Submit
                            </Button>
                        </div>
                    )}
                    {status === 'rejected' && (
                        <div className="selection-toolbox-options">
                            <Button variant="contained" color="error" onClick={() => openAction('resubmit', selectedRows)}>
                                Resubmit
                            </Button>
                        </div>
                    )}
                    {status === 'cancelled' && (
                        <div className="selection-toolbox-options">
                            <Button variant="contained" color="error" onClick={() => openAction('resubmit', selectedRows)}>
                                Resubmit
                            </Button>
                        </div>
                    )}
                    {status === 'blocked' && (
                        <div className="selection-toolbox-options">
                            <Button variant="contained" color="error" onClick={() => openAction('escalate', selectedRows)}>
                                Escalate
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {skeleton && !mobile && (
                <TableSkeleton
                    rows={Math.min(pageSize, 10)}
                    rowHeight={66}
                    headerHeight={58}
                    checkbox={hasCheckboxSelection}
                    columns={[
                        {flex: 0.8, widths: ['70%', '64%']},
                        {flex: 1, widths: ['62%', '48%', '70%', '55%']},
                        {flex: 1, widths: ['55%', '68%', '46%', '60%']},
                        {flex: 1, widths: ['72%', '58%', '66%']},
                        {flex: 0.6, widths: ['18%'], headerWidth: '60%'},
                        {flex: 1, widths: ['78%', '72%']},
                        {flex: 1.2, widths: ['66%', '60%']},
                        ...(status === 'all_status' ? [{flex: 1, type: 'chip', chipWidth: 120}] : []),
                        {flex: 0.8, type: 'chip', chipWidth: 72, headerWidth: '50%'},
                    ]}
                />
            )}
            {skeleton && mobile && <VisitCardsSkeleton checkbox={hasCheckboxSelection} />}
            {!mobile && rows?.length > 0 && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={row => row.visitId}
                    paginationModel={{page, pageSize}}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    getRowHeight={() => 66}
                    style={{display: skeleton ? 'none' : undefined}}
                    columnHeaderHeight={selectedRows?.length > 0 && hasCheckboxSelection ? 0 : 58}
                    checkboxSelection={hasCheckboxSelection}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={model => setRowSelectionModel(model)}
                    onRowClick={e => navigate(`/evv/${e.row?.visitId}`)}
                    slots={{
                        footer: () => (
                            <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                        ),
                    }}
                />
            )}
            {!skeleton && mobile && mobileRows?.length > 0 && (
                <InfiniteScroll
                    dataLength={mobileRows?.length}
                    next={() => setScrollStep(prev => prev + 1)}
                    hasMore={hasMoreScroll}
                    loader={
                        <Box display="flex" justifyContent="center" my={2}>
                            <CircularProgress />
                        </Box>
                    }
                    endMessage={
                        <Typography align="center" sx={{mt: 2}}>
                            All Loaded.
                        </Typography>
                    }>
                    {mobileRows.map(row => (
                        <div className="card" key={row.visitId} onClick={() => navigate(`/evv/${row.visitId}`)}>
                            <div className="top-card">
                                {statusChip(row.status, row.queued)}
                                {hasCheckboxSelection && (
                                    <Checkbox
                                        checked={rowSelectionModel?.ids?.has(row.visitId) || false}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => {
                                            const ids = new Set(rowSelectionModel?.ids)
                                            e.target.checked ? ids.add(row.visitId) : ids.delete(row.visitId)
                                            setRowSelectionModel({type: 'include', ids})
                                        }}
                                    />
                                )}
                            </div>
                            <Typography variant="body2" color="text.primary">
                                {row.service}
                            </Typography>
                            <div className="form">
                                <Typography variant="h8" color="text.primary">
                                    Total Cost
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    -
                                </Typography>
                            </div>
                            <Typography variant="h8" color="text.primary">
                                Client: {row.client?.name}
                            </Typography>
                            <Typography variant="h8" color="text.primary">
                                Caregiver: {row.caregiver?.name}
                            </Typography>
                            <div className="form">
                                <Typography variant="body2" color="text.primary">
                                    {format(new Date(ignoreTimezone(row.date)), 'MM/dd/yyyy')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {formatTime(isoTime(row.scheduled?.start))}-{formatTime(isoTime(row.scheduled?.end))}
                                </Typography>
                            </div>
                            <div className="form" onClick={e => e.stopPropagation()}>
                                {rowAction(row)}
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            )}
            {!skeleton && visibleRows?.length === 0 && (
                <>
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                {emptyState.title}
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            {emptyState.description}
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <VisitActionDialog
                open={Boolean(actionOpen)}
                setOpen={value => {
                    if (!value) setActionOpen(null)
                }}
                action={actionOpen}
                loadData={() => loadData(true)}
                items={actionItems}
            />
        </>
    )
}
